import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, Clock } from 'lucide-react';

const sentimentColor = (score) => {
  if (score === 'Positive') return { bg: 'bg-green-500/10 border-green-500/30', text: 'text-green-400', gradient: 'from-green-400 to-emerald-500' };
  if (score === 'Negative') return { bg: 'bg-red-500/10 border-red-500/30', text: 'text-red-400', gradient: 'from-red-400 to-rose-500' };
  return { bg: 'bg-amber-500/10 border-amber-500/30', text: 'text-amber-400', gradient: 'from-amber-400 to-yellow-500' };
};

const SentimentIcon = ({ score }) => {
  if (score === 'Positive') return <TrendingUp className="w-5 h-5 text-green-400" />;
  if (score === 'Negative') return <TrendingDown className="w-5 h-5 text-red-400" />;
  return <Minus className="w-5 h-5 text-amber-400" />;
};

const MarketSentimentSection = ({ startupId }) => {
  const [sentiment, setSentiment] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    if (startupId) loadHistory();
  }, [startupId]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/startups/${startupId}/sentiment-history`, { withCredentials: true });
      setHistory(res.data);
      if (res.data.length > 0) setSentiment(res.data[0]);
    } catch {} finally {
      setLoadingHistory(false);
    }
  };

  const analyzeSentiment = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/startups/${startupId}/market-sentiment`, { withCredentials: true });
      setSentiment(res.data);
      setHistory(prev => [res.data, ...prev]);
    } catch {
      // LLM key might be missing or quota exceeded
    } finally {
      setLoading(false);
    }
  };

  const colors = sentiment ? sentimentColor(sentiment.sentiment_score) : null;

  return (
    <Card className="bg-slate-800/40 border-slate-700 p-6" data-testid="market-sentiment-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-slate-100">Market Sentiment</h3>
        </div>
        <Button
          onClick={analyzeSentiment}
          disabled={loading}
          size="sm"
          data-testid="analyze-sentiment-btn"
          className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-sm"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Analyzing...</> : <><RefreshCw className="w-4 h-4 mr-1" /> Analyze Market</>}
        </Button>
      </div>

      {loadingHistory ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-cyan-400"></div>
        </div>
      ) : sentiment ? (
        <div className="space-y-4">
          {/* Score Header */}
          <div className={`p-4 rounded-lg border ${colors.bg}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <SentimentIcon score={sentiment.sentiment_score} />
                <span className={`text-lg font-bold ${colors.text}`}>{sentiment.sentiment_score}</span>
              </div>
              <span className={`text-3xl font-bold bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent`}>
                {sentiment.sentiment_percentage}%
              </span>
            </div>
            <p className="text-sm text-slate-400">{sentiment.industry}</p>
          </div>

          {/* Analysis */}
          <p className="text-sm text-slate-300 leading-relaxed">{sentiment.analysis}</p>

          {/* Key Factors */}
          {sentiment.key_factors && sentiment.key_factors.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Key Factors</h4>
              <div className="space-y-1.5">
                {sentiment.key_factors.map((factor, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${colors.text.replace('text-', 'bg-')}`}></div>
                    <span className="text-sm text-slate-400">{factor}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamp */}
          <div className="flex items-center gap-1 text-xs text-slate-600">
            <Clock className="w-3 h-3" />
            {new Date(sentiment.generated_at).toLocaleString()}
          </div>
        </div>
      ) : (
        <div className="text-center py-6">
          <BarChart3 className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-500">Click "Analyze Market" to get AI-powered sentiment analysis for your industry</p>
        </div>
      )}
    </Card>
  );
};

export default MarketSentimentSection;
